import React from "react";
import {
  Box,
  Stack,
  Typography,
  Switch,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Divider,
  Alert,
  Link,
} from "@mui/material";
import { InfoOutlined, OpenInNew, WarningAmber } from "@mui/icons-material";
import {
  ColorsConstants,
  dashboardCardGreenTextColor,
  grayFontColor,
} from "../../../../cssUtils/constants";
import typography from "../../../../cssUtils/typography";
import { StringField } from "../ConfigFields/StringField";
import { formatTitle } from "../utils";
import PaymentLinkStripeSettings from "./PaymentLinkStripeSettings";
import PaymentLinkMessagePreview from "./PaymentLinkMessagePreview";

const GATEWAY_LABELS = {
  stripe: "Stripe",
  cashfree: "Cashfree",
  razorpay: "Razorpay",
  phonepe: "PhonePe",
};

const SUPPORTED_GATEWAYS = ["stripe", "cashfree", "razorpay", "phonepe"];

const MANAGE_INTEGRATIONS_VALUE = "__manage_integrations__";
const INTEGRATIONS_PAGE_KEY = "carepms.all";

const maskAccountId = (accountId) => {
  if (!accountId || accountId.length <= 10) return accountId;
  return `${accountId.slice(0, 8)}...${accountId.slice(-4)}`;
};

const formatGatewaySubLabel = (details) => {
  if (!details?.accountId) return null;
  const modeLabel = details.mode === "live" ? "Live" : "Test";
  return `${maskAccountId(details.accountId)} (${modeLabel})`;
};

const GatewayStatusChip = ({ connected }) => (
  <Chip
    label={connected ? "Connected" : "Not connected"}
    size="small"
    sx={{
      backgroundColor: connected ? "#E6F4EA" : ColorsConstants.secondaryGray,
      color: connected ? dashboardCardGreenTextColor : grayFontColor,
      fontWeight: 600,
      height: 22,
    }}
  />
);

const PaymentLinkPreset = ({
  schema,
  config,
  handleConfigChange,
  enabledPaymentGateways,
  paymentGatewayDetails,
  lastStripeWebhookEvent,
  tenant,
}) => {
  const connectedGateways = enabledPaymentGateways || [];
  const gatewayDetails = paymentGatewayDetails || {};
  const isEnabled = config["enable_payment_gateway"] ?? true;
  const hasConnectedGateway = connectedGateways.length > 0;
  const selectedGateway = config["payment_gateway"] || "";
  const stripeConfig = config["stripe"] || {};

  const goToIntegrations = () => {
    localStorage.setItem("activePage", INTEGRATIONS_PAGE_KEY);
    window.location.reload();
  };

  const handleGatewayChange = (event) => {
    const value = event.target.value;

    if (value === MANAGE_INTEGRATIONS_VALUE) {
      goToIntegrations();
      return;
    }

    handleConfigChange("payment_gateway", value);
  };

  const handleStripeConfigChange = (field, value) => {
    handleConfigChange("stripe", {
      amount_mode: "dynamic",
      ...stripeConfig,
      [field]: value,
    });
  };

  return (
    <Stack spacing={3}>
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
        Configuration:
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{
            ...typography.bodySmall,
            fontWeight: 600,
            textTransform: "uppercase",
            color: grayFontColor,
          }}
        >
          Enable Payment Gateway *
        </Typography>
        <Switch
          checked={isEnabled}
          onChange={(e) =>
            handleConfigChange("enable_payment_gateway", e.target.checked)
          }
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: ColorsConstants.primaryBlue100,
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: ColorsConstants.primaryBlue100,
            },
          }}
        />
      </Stack>

      <StringField
        fieldKey="message"
        schema={schema?.properties?.["message"] || {}}
        value={config["message"]}
        required={false}
        onChange={(val) => handleConfigChange("message", val)}
      />

      {isEnabled && (
        <Box>
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
            <Typography
              sx={{
                ...typography.bodySmall,
                fontWeight: 600,
                textTransform: "uppercase",
                color: grayFontColor,
              }}
            >
              Select Payment Gateway *
            </Typography>
            <Tooltip title="Only gateways connected on the Integrations page can be selected here. Credentials are managed there, not on this node.">
              <InfoOutlined
                sx={{ fontSize: 16, color: ColorsConstants.primaryGray40 }}
              />
            </Tooltip>
          </Stack>

          <Select
            fullWidth
            displayEmpty
            value={config["payment_gateway"] || ""}
            onChange={handleGatewayChange}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography
                    sx={{
                      ...typography.bodyLarge,
                      color: ColorsConstants.primaryGray40,
                    }}
                  >
                    Select a gateway
                  </Typography>
                );
              }

              const connected = connectedGateways.includes(selected);

              return (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography sx={typography.bodyLarge}>
                    {GATEWAY_LABELS[selected] || formatTitle(selected)}
                  </Typography>
                  <GatewayStatusChip connected={connected} />
                </Stack>
              );
            }}
            sx={{ "& .MuiSelect-select": { py: 1.2 } }}
          >
            {SUPPORTED_GATEWAYS.map((gatewayKey) => {
              const connected = connectedGateways.includes(gatewayKey);
              const subLabel = connected
                ? formatGatewaySubLabel(gatewayDetails[gatewayKey])
                : null;

              return (
                <MenuItem
                  key={gatewayKey}
                  value={gatewayKey}
                  disabled={!connected}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Box>
                      <Typography sx={typography.bodyLarge}>
                        {GATEWAY_LABELS[gatewayKey]}
                      </Typography>
                      {subLabel && (
                        <Typography
                          sx={{ ...typography.bodySmall, color: grayFontColor }}
                        >
                          {subLabel}
                        </Typography>
                      )}
                    </Box>
                    <GatewayStatusChip connected={connected} />
                  </Stack>
                </MenuItem>
              );
            })}

            <Divider />

            <MenuItem value={MANAGE_INTEGRATIONS_VALUE}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <OpenInNew
                  sx={{
                    fontSize: 16,
                    color: ColorsConstants.secondaryBackgroundColor,
                  }}
                />
                <Typography
                  sx={{
                    ...typography.bodyLarge,
                    color: ColorsConstants.secondaryBackgroundColor,
                  }}
                >
                  Manage gateways in Integrations
                </Typography>
              </Stack>
            </MenuItem>
          </Select>

          {!hasConnectedGateway && (
            <Alert
              severity="warning"
              icon={<WarningAmber fontSize="inherit" />}
              sx={{ mt: 1.5, "& .MuiAlert-message": typography.bodySmall }}
            >
              No payment gateway is connected yet.{" "}
              <Link
                component="button"
                type="button"
                onClick={goToIntegrations}
                sx={{ ...typography.bodySmall, fontWeight: 600 }}
              >
                Connect one in Integrations
              </Link>{" "}
              before using this node.
            </Alert>
          )}
        </Box>
      )}

      {isEnabled &&
        selectedGateway === "stripe" &&
        connectedGateways.includes("stripe") && (
          <>
            <PaymentLinkStripeSettings
              gatewayDetails={gatewayDetails.stripe}
              stripeConfig={stripeConfig}
              onStripeConfigChange={handleStripeConfigChange}
              lastWebhookEvent={lastStripeWebhookEvent}
              tenant={tenant}
            />

            <PaymentLinkMessagePreview
              messageText={config["message"]}
              buttonLabel={stripeConfig.button_label}
              onButtonLabelChange={(val) => handleStripeConfigChange("button_label", val)}
            />
          </>
        )}
    </Stack>
  );
};

export default PaymentLinkPreset;
